/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100230 配送ルートバッチマスタメンテ
 */
import {
  B000223BatchNm,
  B100230Create,
  B100230CreateRequest,
  B100230Delete,
  B100230DeleteRequest,
  B100230HaisorouteUsedByB100224,
  B100230Update,
  B100230UpdateRequest,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  SText,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import { getSCode, Mode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { B100910 } from "pages";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mode } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  modeKbn: SCode;
  haisorouteNo: string;
  haisorouteNm: string;
  batchNo: string;
  batchNm: string;
  version: number;
  haisorouteNoBefore: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  modeKbn: getSCode(Mode, "0"),
  haisorouteNo: "",
  haisorouteNm: "",
  batchNo: "",
  batchNm: "",
  version: undefined,
  haisorouteNoBefore: "",
};

const focusId = "B100230";
// 画面ID
const gamenId = "100230";

// B100230 配送ルートバッチマスタメンテ
export const B100230 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 更新確認ダイアログオープン用
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);
  // 活性設定
  // 新規モードかどうか
  const isShinki = methods.watch("modeKbn")?.value === mode.shinki;
  // 修正モードかどうか
  const isShusei = methods.watch("modeKbn")?.value === mode.shusei;
  // 削除モードかどうか
  const isSakujo = methods.watch("modeKbn")?.value === mode.sakujo;

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  // 登録
  const handleCreate = () => {
    setOpenUpdateConfirm(false);

    const fields = methods.getValues();

    // 登録するデータの作成
    const param: B100230CreateRequest = {
      haisorouteNo: fields.haisorouteNo!,
      batchNo: fields.batchNo!,
      haisorouteNm: fields.haisorouteNm!,
    };

    B100230Create(param)
      .then(() => {
        // フィールドの初期化
        methods.reset();
        // ↓これがあると完了メッセージが表示される
        methods.setValue("haisorouteNo", "");
        // 登録完了メッセージ表示
        setMsg(getClientMessage("B100230_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 更新
  const handleUpdate = () => {
    setOpenUpdateConfirm(false);

    const fields = methods.getValues();

    // 更新するデータの作成
    const param: B100230UpdateRequest = {
      haisorouteNo: fields.haisorouteNo!,
      batchNo: fields.batchNo!,
      version: fields.version!,
      haisorouteNm: fields.haisorouteNm!,
    };

    B100230Update(param)
      .then(() => {
        // フィールドの初期化
        methods.reset();
        // ↓これがあると完了メッセージが表示される
        methods.setValue("haisorouteNo", "");
        // 更新完了メッセージ表示
        setMsg(getClientMessage("B100230_002"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 削除
  const handleDelete = () => {
    setOpenUpdateConfirm(false);

    const fields = methods.getValues();

    // 削除するデータの作成
    const param: B100230DeleteRequest = {
      haisorouteNo: fields.haisorouteNo!,
    };

    B100230Delete(param)
      .then(() => {
        // フィールドの初期化
        methods.reset();
        // ↓これがあると完了メッセージが表示される
        methods.setValue("haisorouteNo", "");
        // 削除完了メッセージ表示
        setMsg(getClientMessage("B100230_003"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  const handleChangeMode = () => {
    // 条件をisSakujoにすると、セレクトボックス切り替え時に即座に値がクリアされない
    if (methods.watch("modeKbn")?.value === mode.sakujo) {
      methods.setValue("batchNo", "");
      methods.setValue("batchNm", "");
    }
  };

  const handleChangeHaisorouteNo = () => {
    if (isShinki) {
      return;
    } else {
      (async () => {
        await B100230HaisorouteUsedByB100224(methods, navigate, setMsg);
        await B000223BatchNm(methods, navigate, setMsg);
      })();
    }
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="配送ルートバッチマスタメンテ"
          gridTemplateColumns="9rem 9rem 7rem 10rem 5rem 5rem"
        >
          <SText gridColumn="span 5"></SText>
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            {...register(6)}
            dialog={<B100910 gamenId={gamenId} />}
          />
          <SLabel required gridColumn="1">
            モード
          </SLabel>
          <SCodeSelect
            required
            name="modeKbn"
            label="モード"
            options={Mode}
            onChange={() => handleChangeMode()}
            {...register(1)}
          />

          <SLabel required gridColumn="1">
            配送ルート番号
          </SLabel>
          <SNumberField
            name="haisorouteNo"
            label="配送ルート番号"
            code="true"
            required
            size={6}
            {...register(2)}
            onBlur={() => {
              handleChangeHaisorouteNo();
            }}
          />

          <SLabel gridColumn="1">配送ルート名</SLabel>
          <STextField
            name="haisorouteNm"
            label="配送ルート名"
            code
            size={20}
            {...register(3)}
            gridColumn="span 2"
          />

          <SText gridColumn="span 6">　</SText>

          <SLabel required gridColumn="1">
            バッチ番号
          </SLabel>
          <SNumberField
            name="batchNo"
            label="バッチ番号"
            code
            required
            size={6}
            {...register(4)}
            onBlur={() => {
              B000223BatchNm(methods, navigate, setMsg);
            }}
            disabled={isSakujo}
          />
          <STextField
            name="batchNm"
            label="バッチ名"
            disabled
            gridColumn="span 2"
          />

          {isShinki ? (
            <>
              <SNormalButton
                label={labels.buttons.create}
                align="right"
                gridColumn="6"
                gridRow="7"
                {...register(5)}
                dialog={
                  <SConfirmDialog
                    title={labels.dialog.createMasterTitle}
                    msg={labels.dialog.createConfirm}
                  />
                }
                dialogAction="confirm"
                handleSubmit={handleCreate}
              />
            </>
          ) : (
            <>
              {isShusei ? (
                <>
                  <SNormalButton
                    label={labels.buttons.update}
                    align="right"
                    gridColumn="6"
                    gridRow="7"
                    {...register(5)}
                    dialog={
                      <SConfirmDialog
                        title={labels.dialog.updateMasterTitle}
                        msg={labels.dialog.updateConfirm}
                      />
                    }
                    dialogAction="confirm"
                    handleSubmit={handleUpdate}
                  />
                </>
              ) : (
                <>
                  <SCancelButton
                    label={labels.buttons.delete}
                    align="right"
                    gridColumn="6"
                    gridRow="7"
                    {...register(5)}
                    dialog={
                      <SConfirmDialog
                        title={labels.dialog.deleteMasterTitle}
                        msg={labels.dialog.deleteConfirm}
                      />
                    }
                    dialogAction="confirm"
                    handleSubmit={handleDelete}
                  />
                </>
              )}
            </>
          )}
        </SFrame>
      </FormProvider>
    </>
  );
};
